import { useMatches, useRouterState } from '@tanstack/react-router'
import React, { createContext, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { buildBreadcrumbs, findRouteTitle } from './helpers'
import type { IProps, IShellTitleContext, IShellTitleEntry } from './interfaces'

export const ShellTitleContext = createContext<IShellTitleContext | null>(null)

/**
 * Holds the title and subtitle the shell header (AppHeader) shows. A page publishes its own through
 * `useShellTitle` (PageHeader does this); without one the header falls back to the route's
 * `staticData.title`. An entry belongs to the pathname it was published on, so the title resets on
 * every route change.
 */
export const ShellTitleProvider = ({ children }: IProps): React.JSX.Element => {
  const [entry, setEntry] = useState<IShellTitleEntry | null>(null)

  const { t } = useTranslation()
  const matches = useMatches()
  const pathname = useRouterState({ select: (state): string => state.location.pathname })

  const routeTitle = useMemo((): string | undefined => findRouteTitle(matches, t), [matches, t])
  const breadcrumbs = useMemo(() => buildBreadcrumbs(matches, pathname, t), [matches, pathname, t])

  const publish = useCallback((next: IShellTitleEntry): void => setEntry(next), [])
  const retract = useCallback(
    (previous: IShellTitleEntry): void => setEntry((current) => (current === previous ? null : current)),
    [],
  )

  const value = useMemo((): IShellTitleContext => {
    const pageEntry = entry?.pathname === pathname ? entry : null

    return {
      pathname,
      title: pageEntry?.title ?? routeTitle,
      subtitle: pageEntry?.subtitle,
      breadcrumbs,
      publish,
      retract,
    }
  }, [breadcrumbs, entry, pathname, publish, retract, routeTitle])

  return <ShellTitleContext value={value}>{children}</ShellTitleContext>
}
